import { useEffect, useRef, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { cn } from '@/lib/cn'
import { CloseButton } from './CloseCross'

export interface ModalProps {
  open: boolean
  onClose: () => void
  title?: ReactNode
  children: ReactNode
  className?: string
}

/** Accessible modal: focus trap entry, ESC to close, backdrop click, aria-modal. */
export function Modal({ open, onClose, title, children, className }: ModalProps) {
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose()
    document.addEventListener('keydown', onKey)
    ref.current?.focus()
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = prev
    }
  }, [open, onClose])

  if (!open) return null
  // Portal to <body> so the modal escapes the page's stacking contexts (e.g.
  // the <main z-10> wrapper and the Leaflet map's internal z-indexes), which
  // would otherwise paint over a non-portaled, lower-stacked dialog.
  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-obsidian-600/80 p-4 backdrop-blur-sm"
      onMouseDown={(e) => e.target === e.currentTarget && onClose()}
    >
      <div
        ref={ref}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        className={cn('surface-parchment max-h-[90vh] w-full max-w-2xl overflow-y-auto p-6', className)}
      >
        <div className="mb-4 flex items-start justify-between gap-4">
          {title && <h2 className="title-gothic text-xl text-blood-200">{title}</h2>}
          <CloseButton onClick={onClose} label="Close dialog" glyph={20} box={22} className="-m-2 ml-auto text-bone-dim hover:text-bone" />
        </div>
        {children}
      </div>
    </div>,
    document.body,
  )
}
